import { Components, CSSInterpolation, Theme } from '@mui/material'

declare module '@mui/material/Avatar' {
  interface AvatarOwnProps {
    // Avatar doesn't have a color prop in MUI, so we add one
    color?:
      | 'default'
      | 'futureTheme.brand-muted'
      | 'futureTheme.gold'
      | 'futureTheme.mint'
      | 'futureTheme.plum'
      | 'futureTheme.lime'
    // The avatar's dimensions; the font-size scales with it
    size?: 16 | 20 | 24 | 32
    ['data-is-surplus']?: boolean
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface AvatarPropsVariantOverrides {
    // TODO - use when no more usage of rounded variant
    // rounded: false
  }
}

export const MuiAvatar: Components<Omit<Theme, 'components'>>['MuiAvatar'] = {
  defaultProps: {
    variant: 'circular',
    color: 'default',
    size: 24,
    slotProps: {
      root: ({ color }) => ({
        'data-color': color,
        // Don't leak our custom props to the DOM as invalid attributes
        color: undefined,
        size: undefined,
      }),
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderWidth: theme.futureTheme.strokeWidth.default,
      borderStyle: 'solid',
    }),
  },
  variants: [
    // Size — width, height and font-size scale together
    {
      props: { size: 16 },
      style: ({ theme }) => ({
        width: 16,
        height: 16,
        ...theme.typography['futureTheme.extraSmall-semiBold'],
        fontSize: 6,
      }),
    },
    {
      props: { size: 20 },
      style: ({ theme }) => ({
        width: 20,
        height: 20,
        ...theme.typography['futureTheme.extraSmall-semiBold'],
        fontSize: 8,
      }),
    },
    {
      props: { size: 24 },
      style: ({ theme }) => ({
        width: 24,
        height: 24,
        ...theme.typography['futureTheme.extraSmall-semiBold'],
      }),
    },
    {
      props: { size: 32 },
      style: ({ theme }) => ({
        width: 32,
        height: 32,
        ...theme.typography['futureTheme.small-bold'],
      }),
    },
    // Shape
    {
      // Make rounded and square both show as the same thing for now, until we remove the rounded variant from usage
      props: (props) => props.variant === 'square' || props.variant === 'rounded',
      style: ({ theme }) => ({
        borderRadius: theme.shape['futureTheme.radius-sm'],
      }),
    },
    // Colour
    {
      props: { color: 'default' },
      style: ({ theme }) => ({
        borderWidth: 0,
        backgroundColor: theme.palette.futureTheme.bg.brand.default,
        color: theme.palette.futureTheme.text.brand.onBrand,
      }),
    },
    {
      props: { color: 'futureTheme.brand-muted' },
      style: ({ theme }) => ({
        borderWidth: 0,
        backgroundColor: theme.palette.futureTheme.bg.brand.muted,
        color: theme.palette.futureTheme.text.brand.onBrandMuted,
      }),
    },
    {
      props: { color: 'futureTheme.gold' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.gold.muted,
        borderColor: theme.palette.futureTheme.border.utility.gold.muted,
        color: theme.palette.futureTheme.text.utility.gold.default,
      }),
    },
    {
      props: { color: 'futureTheme.mint' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.mint.muted,
        borderColor: theme.palette.futureTheme.border.utility.mint.muted,
        color: theme.palette.futureTheme.text.utility.mint.default,
      }),
    },
    {
      props: { color: 'futureTheme.plum' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.plum.muted,
        borderColor: theme.palette.futureTheme.border.utility.plum.muted,
        color: theme.palette.futureTheme.text.utility.plum.default,
      }),
    },
    {
      props: { color: 'futureTheme.lime' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.lime.muted,
        borderColor: theme.palette.futureTheme.border.utility.lime.muted,
        color: theme.palette.futureTheme.text.utility.lime.default,
      }),
    },
  ],
}
export const MuiAvatarGroup: Components<Omit<Theme, 'components'>>['MuiAvatarGroup'] = {
  defaultProps: {
    spacing: 0,
    slotProps: {
      surplus: {
        'data-is-surplus': true,
      },
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      gap: theme.spacing(1),
    }),
    avatar: ({ theme }) => ({
      // MUI's AvatarGroup forces a 2px border for overlapping stacks; we don't overlap,
      // so strip it and restore each avatar's own per-colour border via data-color.
      border: 'none',
      ...(['gold', 'mint', 'plum', 'lime'] as const).reduce<Record<string, CSSInterpolation>>((acc, color) => {
        acc[`&[data-color="futureTheme.${color}"]`] = {
          borderStyle: 'solid',
          borderWidth: theme.futureTheme.strokeWidth.default,
          borderColor: theme.palette.futureTheme.border.utility[color].muted,
        }
        return acc
      }, {}),
      '&[data-is-surplus="true"]': {
        backgroundColor: theme.palette.futureTheme.bg.invisible,
        color: theme.palette.futureTheme.text.muted,
      },
    }),
  },
}
